import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetProcuremetThreadsQuery } from "../../../store/apiSlices/procurementAI";
import { Thread } from "../../../types/ProcurementChat";

type ChatMessage = {
  id: string;
  role: "user" | "ai";
  text?: string;
  typing?: boolean;
};

function transformMessages(
  thread: Thread | undefined,
  isTyping: boolean,
): ChatMessage[] {
  if (!thread) return [];

  const result: ChatMessage[] = [];

  thread.messages.forEach((msg) => {
    if (msg.user_chat) {
      result.push({
        id: `user-${msg.id}`,
        role: "user",
        text: msg.user_chat,
      });
    }

    if (msg.ai_chat) {
      result.push({
        id: `ai-${msg.id}`,
        role: "ai",
        text: msg.ai_chat,
      });
    }
  });

  // ONLY append typing at the end
  if (isTyping) {
    result.push({
      id: "typing",
      role: "ai",
      typing: true,
    });
  }

  return result;
}

export default function ChatMessages({ isLoading }: { isLoading: boolean }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const activeThreadId = searchParams.get("threadId");

  const { activeThread } = useGetProcuremetThreadsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      activeThread: data?.find((t) => t.thread_id === activeThreadId),
    }),
  });

  const messages = useMemo(() => {
    return transformMessages(activeThread, isLoading);
  }, [activeThread, isLoading]);

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // console.log(messages);

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3" ref={containerRef}>
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex ${
            msg.role === "user" ? "justify-end" : "justify-start"
          }`}
        >
          <div
            className={`px-3 py-2 rounded-lg text-sm max-w-[70%] ${
              msg.role === "user"
                ? "bg-custom-blue-medium text-white"
                : "bg-gray-100 text-[#475467]"
            }`}
          >
            {msg.typing ? (
              <TypingDots />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: (msg.text || "")
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\n/g, "<br />"),
                }}
                className="max-w-[400px]"
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex gap-1 items-center">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
    </div>
  );
}
